import { useSearchParams } from "react-router-dom";
import { ENGAGE_PATHS, PATH_IDS, resolvePath } from "@/content/engage";
import { ROUTES, keyForPath } from "@/content/routes";
import { PageIntro } from "@/components/site/PageIntro";
import { Swap } from "@/components/site/ExplorerFrame";
import { Container, Mono } from "@/components/site/primitives";
import { InquiryForm } from "@/components/engage/InquiryForm";
import { cn } from "@/lib/utils";

const PathList = ({ sel, onSelect }) => (
  <div role="group" aria-label="Inquiry paths" className="grid gap-2 sm:grid-cols-2 lg:sticky lg:top-28 lg:grid-cols-1" data-testid="engage-path-list">
    {PATH_IDS.map((id) => {
      const p = ENGAGE_PATHS[id];
      const on = sel === id;
      return (
        <button key={id} type="button" onClick={() => onSelect(id)} aria-pressed={on} data-testid={`engage-path-${id}`}
          className={cn("flex items-start gap-4 border p-4 text-left transition-colors", on ? "border-o-accent bg-o-soft" : "border-o-line bg-o-surface hover:border-o-strong")}>
          <Mono accent={on}>{p.index}</Mono>
          <span>
            <span className="block font-display text-lg font-bold leading-tight">{p.title}</span>
            <span className="mt-1 block text-sm text-o-dim">{p.forWhom}</span>
          </span>
        </button>
      );
    })}
  </div>
);

const OriginNotice = ({ label, path }) => (
  <div className="mb-6 flex flex-wrap items-baseline gap-x-3 gap-y-1 border-l-2 border-o-accent bg-o-surface px-4 py-3 text-sm" data-testid="engage-origin-notice">
    <Mono accent>Arrived from</Mono>
    <span><span className="font-semibold">{label}</span>. Suggested path: <span className="font-semibold">{path.title}</span>; you can choose another.</span>
  </div>
);

const PathIntro = ({ path }) => (
  <div data-testid="engage-path-intro">
    <Mono accent>Path {path.index}</Mono>
    <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl" data-testid="engage-path-title">{path.title}</h2>
    <p className="mt-4 max-w-2xl leading-relaxed text-o-dim">{path.description}</p>
    <div className="mt-6">
      <Mono>This conversation could cover</Mono>
      <ul className="mt-3 grid gap-2 sm:grid-cols-3">
        {path.covers.map((c) => <li key={c} className="border border-o-line bg-o-surface p-3 text-sm">{c}</li>)}
      </ul>
    </div>
  </div>
);

export default function EngagePage() {
  const [params, setParams] = useSearchParams();
  const from = params.get("from") || "";
  const sel = resolvePath(params.get("path"), from);
  const path = ENGAGE_PATHS[sel];
  const originKey = from ? keyForPath(from) : null;
  const originLabel = originKey ? (originKey === "home" ? "The homepage" : ROUTES[originKey].label) : null;
  const choose = (id) => {
    const next = new URLSearchParams(params);
    next.set("path", id);
    setParams(next, { replace: true });
  };
  return (
    <>
      <PageIntro routeKey="engage" index="EN" eyebrow="Engagement · Contextual inquiry" title="Begin from the part of the work that concerns you."
        lede="Ordo Intelligence is being formed around an already developed body of work. Choose the relationship you want to initiate; each path asks only what that conversation needs." />
      <section data-testid="engage-section">
        <Container className="grid gap-8 py-14 lg:grid-cols-12 lg:py-20">
          <aside className="lg:col-span-4"><PathList sel={sel} onSelect={choose} /></aside>
          <div className="lg:col-span-8">
            {originLabel && <OriginNotice label={originLabel} path={ENGAGE_PATHS[resolvePath(null, from)]} />}
            <Swap k={sel}>
              <PathIntro path={path} />
              <InquiryForm path={path} from={from} originLabel={originLabel} />
            </Swap>
          </div>
        </Container>
      </section>
      <section className="border-t border-o-line">
        <Container className="py-10">
          <p className="max-w-3xl text-sm text-o-dim" data-testid="engage-phase-note">
            No contact addresses are published during Phase 1, and these forms are not connected to any destination. Final inquiry wording, routing, privacy requirements, and submission behavior will be established in a later phase.
          </p>
        </Container>
      </section>
    </>
  );
}
